import { ArrowRight, TrendingUp, Timer, Users } from "lucide-react";
import { useTranslation } from "react-i18next";

// ─────────────────────────────────────────────────────────────────────────────
// RESIDENCY CASE STUDIES
//
// Live before/after case studies from recent Residency intakes. Each case
// documents: whin the lab saw, what we did with the applicant, what
// happened next, and how long it took.
//
// ADDING NEW PHOTOS
//   Each case study has a `beforeUrl` / `afterUrl` (photo cases) or a
//   `singleUrl` (week-of-profits). Drop the customer-assets URL into the
//   corresponding field. If a URL is null the card renders in "story-only"
//   mode with a clean geometric placeholder — no broken images, no missing
//   sections. When photos land, they slot in cleanly.
// ─────────────────────────────────────────────────────────────────────────────
const CASE_STUDIES = [
  {
    id: "insane-rr",
    kickerKey: "caseStudiesUi.insaneRr.kicker",
    titleKey: "caseStudiesUi.insaneRr.title",
    narrativeKey: "caseStudiesUi.insaneRr.narrative",
    durationKey: "caseStudiesUi.insaneRr.duration",
    metricKey: "caseStudiesUi.insaneRr.metric",
    tagKey: "caseStudiesUi.insaneRr.tag",
    aspect: "portrait",
    beforeUrl:
      "https://customer-assets.emergentagent.com/job_wealth-academy-46/artifacts/w6ty2pkw_IMG_8900.jpeg",
    afterUrl:
      "https://customer-assets.emergentagent.com/job_wealth-academy-46/artifacts/etp8ss94_IMG_8899.jpeg",
    beforeCaptionKey: "caseStudiesUi.insaneRr.beforeCaption",
    afterCaptionKey: "caseStudiesUi.insaneRr.afterCaption",
  },
  {
    id: "ath-management",
    kickerKey: "caseStudiesUi.athManagement.kicker",
    titleKey: "caseStudiesUi.athManagement.title",
    narrativeKey: "caseStudiesUi.athManagement.narrative",
    durationKey: "caseStudiesUi.athManagement.duration",
    metricKey: "caseStudiesUi.athManagement.metric",
    tagKey: "caseStudiesUi.athManagement.tag",
    aspect: "portrait",
    beforeUrl:
      "https://customer-assets.emergentagent.com/job_wealth-academy-46/artifacts/3dqqo45q_IMG_8902.jpeg",
    afterUrl:
      "https://customer-assets.emergentagent.com/job_wealth-academy-46/artifacts/agf7zjua_IMG_8903.jpeg",
    beforeCaptionKey: "caseStudiesUi.athManagement.beforeCaption",
    afterCaptionKey: "caseStudiesUi.athManagement.afterCaption",
  },
  {
    id: "residency-week",
    kickerKey: "caseStudiesUi.residencyWeek.kicker",
    titleKey: "caseStudiesUi.residencyWeek.title",
    narrativeKey: "caseStudiesUi.residencyWeek.narrative",
    durationKey: "caseStudiesUi.residencyWeek.duration",
    metric: "+$145,648.20",
    tagKey: "caseStudiesUi.residencyWeek.tag",
    aspect: "landscape",
    singleUrl:
      "https://customer-assets.emergentagent.com/job_wealth-academy-46/artifacts/bjfsuqpj_IMG_8901.jpeg",
    singleCaptionKey: "caseStudiesUi.residencyWeek.singleCaption",
  },
];

function GeometricPlaceholder() {
  const { t } = useTranslation();
  // A neutral, on-brand placeholder for slots where the photo hasn't landed
  // yet. Uses the same amber/blue radial as the residency hero so it reads
  // as "future proof" rather than "broken".
  return (
    <div className="relative w-full h-full min-h-[220px] rounded-xl overflow-hidden bg-slate-950 border border-slate-800">
      <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_25%_30%,rgba(30,64,175,0.55),transparent_55%),radial-gradient(circle_at_80%_75%,rgba(245,158,11,0.35),transparent_50%)]" />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6">
        <div className="w-10 h-10 rounded-full border border-amber-500/40 bg-amber-500/10 flex items-center justify-center">
          <TrendingUp size={16} className="text-amber-400" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300 font-semibold">
          {t("caseStudiesUi.placeholderTitle")}
        </div>
        <div className="text-xs text-slate-400 max-w-[220px] leading-relaxed">
          {t("caseStudiesUi.placeholderDescription")}
        </div>
      </div>
    </div>
  );
}

function ImageSlot({ url, alt, caption, label, aspect = "portrait" }) {
  const aspectClass = aspect === "portrait" ? "aspect-[3/4]" : "aspect-[16/10]";
  return (
    <figure className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400 font-semibold mb-2">
        {label}
      </div>
      {url ? (
        <div className={`relative ${aspectClass} rounded-xl overflow-hidden border border-slate-800 bg-slate-950`}>
          <img src={url} alt={alt} className="absolute inset-0 w-full h-full object-contain" />
        </div>
      ) : (
        <GeometricPlaceholder />
      )}
      {caption && (
        <figcaption className="mt-3 text-xs text-slate-400 leading-relaxed">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

function CaseStudy({ study }) {
  const { t } = useTranslation();
  const isPair = "beforeUrl" in study;
  const aspect = study.aspect || "portrait";
  return (
    <article
      data-testid={`residency-case-${study.id}`}
      className="relative overflow-hidden rounded-3xl bg-slate-950 text-white border border-amber-500/20"
    >
      <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_10%_20%,rgba(30,64,175,0.55),transparent_55%),radial-gradient(circle_at_90%_80%,rgba(245,158,11,0.35),transparent_50%)]" />
      <div className="relative p-8 sm:p-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          {/* Narrative column */}
          <div className="lg:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 font-semibold">
              {t(study.kickerKey)}
            </div>
            <h3 className="mt-3 font-display font-black text-3xl sm:text-4xl tracking-tighter leading-tight">
              {t(study.titleKey)}
            </h3>
            <p className="mt-5 text-slate-300 text-sm leading-relaxed">
              {t(study.narrativeKey)}
            </p>
            <dl className="mt-8 grid grid-cols-2 gap-4 border-t border-slate-800 pt-6">
              <div>
                <dt className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold flex items-center gap-1.5">
                  <Timer size={11} /> {t("caseStudiesUi.durationLabel")}
                </dt>
                <dd className="mt-1 font-display font-bold text-lg text-white">
                  {t(study.durationKey)}
                </dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold flex items-center gap-1.5">
                  <TrendingUp size={11} /> {t("caseStudiesUi.outcomeLabel")}
                </dt>
                <dd className="mt-1 font-display font-bold text-lg text-amber-400">
                  {study.metricKey ? t(study.metricKey) : study.metric}
                </dd>
              </div>
            </dl>
            <div className="mt-5 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold border border-slate-700 rounded-full px-3 py-1.5 bg-slate-900/40">
              <Users size={11} /> {t(study.tagKey)}
            </div>
          </div>

          {/* Photo column */}
          <div className="lg:col-span-3">
            {isPair ? (
              <div className="flex flex-col sm:flex-row items-stretch gap-4 sm:gap-3">
                <ImageSlot
                  url={study.beforeUrl}
                  alt={`${t(study.titleKey)} — ${t("caseStudiesUi.before")}`}
                  caption={study.beforeCaptionKey ? t(study.beforeCaptionKey) : study.beforeCaption}
                  label={t("caseStudiesUi.beforeLabel")}
                  aspect={aspect}
                />
                <div className="hidden sm:flex items-center justify-center pt-8 text-amber-400 shrink-0">
                  <ArrowRight size={22} />
                </div>
                <ImageSlot
                  url={study.afterUrl}
                  alt={`${t(study.titleKey)} — ${t("caseStudiesUi.after")}`}
                  caption={study.afterCaptionKey ? t(study.afterCaptionKey) : study.afterCaption}
                  label={t("caseStudiesUi.afterLabel")}
                  aspect={aspect}
                />
              </div>
            ) : (
              <ImageSlot
                url={study.singleUrl}
                alt={t(study.titleKey)}
                caption={study.singleCaptionKey ? t(study.singleCaptionKey) : study.singleCaption}
                label={t("caseStudiesUi.weekCapturedLabel")}
                aspect={aspect}
              />
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

export default function ResidencyCaseStudies() {
  const { t } = useTranslation();
  return (
    <section
      data-testid="residency-case-studies"
      className="bg-slate-100 border-y border-slate-200 py-24"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="max-w-3xl mb-14">
          <div className="text-xs uppercase tracking-[0.28em] text-amber-600 font-semibold mb-3">
            {t("caseStudiesUi.sectionKicker")}
          </div>
          <h2 className="font-display font-black text-4xl sm:text-5xl text-slate-900 tracking-tighter leading-tight">
            {t("caseStudiesUi.sectionTitle")}
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            {t("caseStudiesUi.sectionDescription")}
          </p>
        </div>

        <div className="space-y-8">
          {CASE_STUDIES.map((study) => (
            <CaseStudy key={study.id} study={study} />
          ))}
        </div>

        <p className="mt-10 text-xs text-slate-500 max-w-2xl">
          {t("caseStudiesUi.disclaimer")}
        </p>
      </div>
    </section>
  );
}
